import { useState, useEffect, useMemo } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';
import { useProgression } from './useProgression';
import { useStreakTracker } from './useStreakTracker';

interface WeeklySummary {
  weekStartDate: string;
  roomsVisited: string[];
  timeSpentMinutes: number;
  fragmentsCollected: number;
  challengesCompleted: string[];
  mostVisitedCategory: string | null;
  insightMessage: string;
  streakDays: number;
}

interface WeeklySummaryState {
  currentWeek: WeeklySummary;
  previousWeeks: WeeklySummary[];
  lastUpdated: string;
}

const STORAGE_KEY = 'human-rooms-weekly-summary';

const getWeekStartDate = (date: Date = new Date()): string => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

const getDefaultState = (): WeeklySummaryState => ({
  currentWeek: {
    weekStartDate: getWeekStartDate(),
    roomsVisited: [],
    timeSpentMinutes: 0,
    fragmentsCollected: 0,
    challengesCompleted: [],
    mostVisitedCategory: null,
    insightMessage: '',
    streakDays: 0,
  },
  previousWeeks: [],
  lastUpdated: new Date().toISOString(),
});

const INSIGHT_MESSAGES = {
  explorer: [
    "You explored broadly this week. The wanderer sees more.",
    "Many rooms, many truths. Some stick.",
    "Your path was wide this week.",
  ],
  focused: [
    "Depth over breadth this week.",
    "You returned to what matters.",
    "Same rooms, deeper understanding.",
  ],
  absent: [
    "The rooms waited for you.",
    "Silence has its own lessons.",
    "Some weeks are for living, not reflecting.",
  ],
  active: [
    "A full week of presence.",
    "You showed up. That's everything.",
    "The rooms noticed you.",
  ],
  streaker: [
    "Consistency reveals patterns.",
    "Day after day, you return.",
    "The habit is forming.",
  ],
  fragmentCollector: [
    "You gathered pieces of yourself.",
    "Each fragment is a small truth.",
    "The collection grows.",
  ],
};

const generateInsight = (summary: WeeklySummary): string => {
  const { roomsVisited, streakDays, fragmentsCollected, timeSpentMinutes } = summary;
  
  if (roomsVisited.length === 0) {
    return INSIGHT_MESSAGES.absent[Math.floor(Math.random() * INSIGHT_MESSAGES.absent.length)];
  }
  
  if (streakDays >= 5) {
    return INSIGHT_MESSAGES.streaker[Math.floor(Math.random() * INSIGHT_MESSAGES.streaker.length)];
  }
  
  if (fragmentsCollected >= 3) {
    return INSIGHT_MESSAGES.fragmentCollector[Math.floor(Math.random() * INSIGHT_MESSAGES.fragmentCollector.length)];
  }
  
  if (roomsVisited.length >= 7) {
    return INSIGHT_MESSAGES.explorer[Math.floor(Math.random() * INSIGHT_MESSAGES.explorer.length)];
  }
  
  const uniqueRooms = new Set(roomsVisited).size;
  if (uniqueRooms < roomsVisited.length / 2) {
    return INSIGHT_MESSAGES.focused[Math.floor(Math.random() * INSIGHT_MESSAGES.focused.length)];
  }
  
  return INSIGHT_MESSAGES.active[Math.floor(Math.random() * INSIGHT_MESSAGES.active.length)];
};

export const useWeeklySummary = () => {
  const { state: progressState } = useProgression();
  const { currentStreak, visitHistory } = useStreakTracker();
  const [summaryState, setSummaryState] = useState<WeeklySummaryState>(getDefaultState);

  // Load from storage
  useEffect(() => {
    const stored = safeGetJSON<WeeklySummaryState>(STORAGE_KEY, getDefaultState());
    const currentWeekStart = getWeekStartDate();
    
    // Check if we need to roll over to a new week
    if (stored.currentWeek.weekStartDate !== currentWeekStart) {
      // Archive the old week if it has data
      const newPreviousWeeks = stored.currentWeek.roomsVisited.length > 0
        ? [stored.currentWeek, ...stored.previousWeeks.slice(0, 11)] // Keep last 12 weeks
        : stored.previousWeeks;
      
      const newState: WeeklySummaryState = {
        currentWeek: {
          weekStartDate: currentWeekStart,
          roomsVisited: [],
          timeSpentMinutes: 0,
          fragmentsCollected: 0,
          challengesCompleted: [],
          mostVisitedCategory: null,
          insightMessage: '',
          streakDays: 0,
        },
        previousWeeks: newPreviousWeeks,
        lastUpdated: new Date().toISOString(),
      };
      setSummaryState(newState);
      safeSetJSON(STORAGE_KEY, newState);
    } else {
      setSummaryState(stored);
    }
  }, []);

  // Update current week from progression state
  useEffect(() => {
    const currentWeekStart = getWeekStartDate();
    const weekStartMs = new Date(currentWeekStart).getTime();
    
    // Filter rooms completed this week
    const weekRooms = progressState.completedRooms;
    const weekFragments = progressState.fragments.filter(f => f.collectedAt >= weekStartMs);
    const weekChallenges = progressState.completedChallenges;
    
    // Calculate most visited category
    const categoryCount: Record<string, number> = {};
    // This is simplified - in real impl would track visits per room
    
    // Count streak days this week
    const weekDates = visitHistory.filter(date => date >= currentWeekStart);
    
    setSummaryState(prev => {
      const updatedWeek: WeeklySummary = {
        ...prev.currentWeek,
        roomsVisited: weekRooms,
        fragmentsCollected: weekFragments.length,
        challengesCompleted: weekChallenges,
        timeSpentMinutes: Math.round(progressState.totalTimeSpent / 60),
        streakDays: weekDates.length,
        insightMessage: prev.currentWeek.insightMessage || generateInsight({
          ...prev.currentWeek,
          roomsVisited: weekRooms,
          streakDays: weekDates.length,
          fragmentsCollected: weekFragments.length,
        }),
      };
      
      const newState = {
        ...prev,
        currentWeek: updatedWeek,
        lastUpdated: new Date().toISOString(),
      };
      
      safeSetJSON(STORAGE_KEY, newState);
      return newState;
    });
  }, [progressState.completedRooms, progressState.fragments, progressState.completedChallenges, progressState.totalTimeSpent, visitHistory]);

  const getWeeklyStats = useMemo(() => ({
    roomsThisWeek: summaryState.currentWeek.roomsVisited.length,
    fragmentsThisWeek: summaryState.currentWeek.fragmentsCollected,
    minutesThisWeek: summaryState.currentWeek.timeSpentMinutes,
    streakDaysThisWeek: summaryState.currentWeek.streakDays,
    insight: summaryState.currentWeek.insightMessage,
    weekNumber: Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)),
  }), [summaryState.currentWeek]);

  const getPreviousWeekComparison = useMemo(() => {
    if (summaryState.previousWeeks.length === 0) return null;
    
    const lastWeek = summaryState.previousWeeks[0];
    const current = summaryState.currentWeek;
    
    return {
      roomsDiff: current.roomsVisited.length - lastWeek.roomsVisited.length,
      fragmentsDiff: current.fragmentsCollected - lastWeek.fragmentsCollected,
      streakDiff: current.streakDays - lastWeek.streakDays,
      isMoreActive: current.roomsVisited.length > lastWeek.roomsVisited.length,
    };
  }, [summaryState]);

  return {
    currentWeek: summaryState.currentWeek,
    previousWeeks: summaryState.previousWeeks,
    getWeeklyStats,
    getPreviousWeekComparison,
    refreshInsight: () => {
      setSummaryState(prev => {
        const newInsight = generateInsight(prev.currentWeek);
        const newState = {
          ...prev,
          currentWeek: {
            ...prev.currentWeek,
            insightMessage: newInsight,
          },
        };
        safeSetJSON(STORAGE_KEY, newState);
        return newState;
      });
    },
  };
};
